import React, { useEffect, useState } from 'react';
import {
    useNavigate,
    useSearchParams,
    createSearchParams,
} from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import Loader from '../loader/loader';
import DataGridSearchForm from '../datagridSearchForm';
import DataGridTable from './datagridTable';

import { dataGridFetchData } from '../../services/datagrid';
import {
    setSearchColumns,
    setSearchFilters,
    setSearchSorting,
    selectSearchColumns,
    selectSearchOptions,
    selectSearchSorting,
    selectSearchFilters,
} from '../../store/slices/datagridSlice';
import {
    dataGridGetOptionFromSearchParams,
    dataGridGenerateQLString,
    dataGridBuildFiltersFromQueryString,
} from '../../utilities/datagrid';

function DataGrid() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const searchColumns = useAppSelector(selectSearchColumns);
    const searchOptions = useAppSelector(selectSearchOptions);
    const searchFilters = useAppSelector(selectSearchFilters);
    const searchColumnSorting = useAppSelector(selectSearchSorting);
    const [loading, setLoading] = useState(false);
    const [searchResponse, setSearchResponse] = useState({});
    const [tableData, setTableData] = useState([]);
    const [tableColumns, setTableColumns] = useState([]);

    useEffect(() => {
        if (searchOptions) {
            const cols =
        dataGridGetOptionFromSearchParams(searchParams, 'columns') || [];
            const columnSorting =
        dataGridGetOptionFromSearchParams(searchParams, 'ordering') || [];
            const queryStringFilters = dataGridGetOptionFromSearchParams(
                searchParams,
                'filters'
            );
            const initialFilters = dataGridBuildFiltersFromQueryString(
                queryStringFilters,
                searchOptions
            );

            dispatch(setSearchFilters(initialFilters));
            dispatch(setSearchColumns(cols));
            dispatch(setSearchSorting(columnSorting));
            if (cols.length) {
                doSearch(cols, initialFilters, columnSorting);
            }
        }
    }, [searchOptions]);

    useEffect(() => {
        if (!searchResponse.error && searchResponse.data) {
            handleSearchResponse(searchResponse?.metadata, searchResponse.data);
        }
    }, [searchResponse]);

    const handleSearchResponse = (metadata, data) => {
        const cols = metadata?.schema?.filter((s) => s.name != 'schema');
        const tblColumns = [];

        for (let col of searchColumns) {
            const [table, field] = col.split('.');
            if (table && field) {
                const def = {
                    field: `${table}.${field}`,
                    headerName: `${table}&${field}`,
                    type: searchOptions[table][field]?.type || 'text',
                };
                tblColumns.push(def);
            }
        }

        tblColumns.push({ field: 'id' });
        setTableColumns(tblColumns);
        const rowData = data.map((r, index) => {
            const row = {};
            row.id = index;
            for (let i = 0; i < r.length; ++i) {
                let c = r[i];
                if (cols[i].type === 'INT64') {
                    c = Number(r[i]);
                }
                row[cols[i].name] = c;
            }
            return row;
        });
        setTableData(rowData);
    };

    const doSearch = async (
        selectedColumns,
        selectedFilters,
        selectedSorting
    ) => {
        setLoading(true);
        setSearchResponse({});
        const payload = {
            columns: selectedColumns,
            filters: dataGridGenerateQLString(selectedFilters, searchOptions),
            ordering: selectedSorting,
        };
        const resp = await dataGridFetchData(payload);
        setSearchResponse(resp);
        setLoading(false);
        return payload;
    };

    const handleSearch = async () => {
        const payload = await doSearch(
            searchColumns,
            searchFilters,
            searchColumnSorting
        );
        navigate(
            {
                pathname: '/datagrid',
                search: createSearchParams({
                    query: encodeURI(JSON.stringify(payload)),
                }).toString(),
            },
            {
                replace: true,
            }
        );
    };

    const renderTableSection = () => {
        if (loading) {
            return <Loader />;
        } else if (searchResponse.error) {
            return <p>{searchResponse.error}</p>;
        } else if (tableColumns.length) {
            if (tableData.length) {
                return <DataGridTable columns={tableColumns} rows={tableData} />;
            } else {
                return <p>No results found.</p>;
            }
        }
        return null;
    };

    return (
        <div className="px-0 px-md-4 py-1 d-flex flex-column gap-4">
            <DataGridSearchForm onSearch={handleSearch} />
            <div>{renderTableSection()}</div>
        </div>
    );
}

export default DataGrid;
